import { Button, ScrollShadow, Spinner, cn } from '@heroui/react';
import { Plus } from 'iconoir-react';

import type { TestCaseSelection } from './types';

import i18n from '~/common/helpers/i18n';
import type { CollectTestCaseListItem } from '~/scenes/Monitor/scenes/AgentLogs/utils/normalizeDatasetExperiments';

type TestCaseSidebarProps = {
  testCases: CollectTestCaseListItem[];
  selection: TestCaseSelection;
  isLoading: boolean;
  onSelectNew: () => void;
  onSelectExisting: (experimentId: string) => void;
};

export function TestCaseSidebar({
  testCases,
  selection,
  isLoading,
  onSelectNew,
  onSelectExisting
}: TestCaseSidebarProps) {
  const isNewSelected = selection.type === 'new';

  return (
    <aside
      className='flex h-full min-h-0 w-[200px] shrink-0 flex-col gap-7 overflow-hidden'
      aria-label={i18n.t('scenes.monitor.agentLogs.collect.testCasesSection')}
      data-testid='monitor-agent-logs-collect-test-case-sidebar'
    >
      <div className='flex shrink-0 flex-col gap-2.5'>
        <p className='text-label-md font-semibold uppercase tracking-[1px] text-on-surface'>
          {i18n.t('scenes.monitor.agentLogs.collect.testCasesSection')}
        </p>
        <Button
          variant='light'
          onPress={onSelectNew}
          startContent={<Plus className='icon-smallest' />}
          className={cn(
            'h-auto min-h-0 justify-start rounded-md px-2 py-1.5 text-title-sm',
            isNewSelected
              ? 'bg-surface-container-low font-medium text-on-surface'
              : 'bg-transparent font-normal text-on-surface'
          )}
          data-testid='monitor-agent-logs-collect-new-test-case'
        >
          {i18n.t('scenes.monitor.agentLogs.collect.newTestCase')}
        </Button>
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-1'>
        <p className='shrink-0 text-label-md font-semibold uppercase tracking-[1px] text-on-surface'>
          {i18n.t('scenes.monitor.agentLogs.collect.currentTestCasesSection')}
        </p>

        {isLoading ? (
          <div
            className='flex items-center justify-center py-6'
            data-testid='monitor-agent-logs-collect-test-cases-loading'
          >
            <Spinner size='sm' />
          </div>
        ) : (
          <ScrollShadow
            className='min-h-0 flex-1'
            size={32}
            orientation='vertical'
            data-testid='monitor-agent-logs-collect-test-case-list'
          >
            <ul className='flex flex-col gap-0.5'>
              {testCases.map(testCase => {
                const isSelected =
                  selection.type === 'existing' &&
                  selection.experimentId === testCase.id;

                return (
                  <li key={testCase.id}>
                    <Button
                      variant='light'
                      onPress={() => onSelectExisting(testCase.id)}
                      className={cn(
                        'h-auto min-h-0 w-full justify-start rounded-md px-2 py-1.5 text-body-regular-md',
                        isSelected
                          ? 'bg-surface-container-low font-medium text-on-surface'
                          : 'bg-transparent font-normal text-on-surface'
                      )}
                      data-testid={`monitor-agent-logs-collect-test-case-${testCase.id}`}
                    >
                      <span className='truncate'>{testCase.name}</span>
                    </Button>
                  </li>
                );
              })}
            </ul>
          </ScrollShadow>
        )}
      </div>
    </aside>
  );
}
